import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CellProps, Column } from "react-table";

import { ActionCell } from "../../../components/ActionCell/ActionCell";
import { DateColumn } from "../../../components/CustomizedTable/Columns";
import type { IData } from "../../../components/CustomizedTable/PaginatedTable";
import { PaginatedTable } from "../../../components/CustomizedTable/PaginatedTable";
import { TableCellSm, TableLineSm } from "../../../components/TableLineSm";
import { TableSearch } from "../../../components/TableSearch";
import { useMediaQueryContext } from "../../../contexts/mediaQueryProvider/MediaQueryProvider";
import { useOrganizationContext } from "../../../contexts/organizations/organizationsContext";
import type { IApplication } from "../../../hooks/reactQueryHooks/useApplications";
import { useDeleteApplication } from "../../../hooks/reactQueryHooks/useApplications";
import { useCheckAccess } from "../../../hooks/useCheckAccess";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { DeleteApplicationModal } from "./modals/deleteApplicationModal/DeleteApplicationModal";
import { EditApplicationModal } from "./modals/editApplicationModal/EditApplicatioModal";

interface IProps {
  data?: IData[];
  isLoading: boolean;
}

const ApplicationTable: React.FC<IProps> = ({ data, isLoading }) => {
  const { currentOrganization } = useOrganizationContext();
  const [showDeleteAppModal, setShowDeleteAppModal] = useState<boolean>(false);
  const [showEditAppModal, setShowEditAppModal] = useState<boolean>(false);
  const [currentApp, setCurrentApp] = useState<IApplication | null>(null);
  const [searchResult, setSearchResult] = useState<IApplication[] | null>(null);
  const { isLg } = useMediaQueryContext();
  const deleteMutation = useDeleteApplication();

  const navigate = useNavigate();

  const { queryValue: searchString } = useQueryParams({
    queryStr: "s",
  });

  const noSubscriptionError = `Deleting projects is disabled while there is no active subscription for the ${currentOrganization?.name} organization.`;

  const openEditModal = useCallback(
    (app: IApplication) => {
      setCurrentApp(app);
      setShowEditAppModal(true);
    },
    [setCurrentApp, setShowEditAppModal],
  );

  const openDeleteModal = useCallback(
    (app: IApplication) => {
      setCurrentApp(app);
      setShowDeleteAppModal(true);
      deleteMutation.reset();
    },
    [setCurrentApp, deleteMutation],
  );

  const renderActions = useCallback(
    (props: CellProps<IData>) => (
      <ActionCell
        onDeleteClick={() =>
          openDeleteModal({
            id: props.row.original.id,
            name: props.row.original.name,
          })
        }
        onEditClick={() => {
          openEditModal({
            id: props.row.original.id,
            name: props.row.original.name,
            settings: props.row.original.settings,
          });
        }}
      />
    ),
    [openDeleteModal, openEditModal],
  );

  const renderDate = (props: CellProps<IData>) => (
    <DateColumn type="short" date={props.row.original.created} />
  );

  const renderSM = (props: CellProps<IData>) => (
    <TableLineSm
      title={
        <div className="d-flex w-auto align-items-center">
          {props.row.original.name}
        </div>
      }
      controls={renderActions(props)}
    >
      <TableCellSm title="Project ID">{props.row.original.id}</TableCellSm>
      <TableCellSm title="Created">{renderDate(props)}</TableCellSm>
    </TableLineSm>
  );

  const columns: Column<IData>[] = [
    {
      Header: "Name",
      accessor: "name",
      minWidth: 40,
      width: 440,
    },
    {
      Header: "Project ID",
      accessor: "id",
      minWidth: 140,
      width: 300,
    },
    {
      Header: "Creation date",
      accessor: "created",
      minWidth: 140,
      Cell: renderDate,
    },
    {
      Header: "",
      id: "edit",
      minWidth: 50,
      width: 50,
      maxWidth: 50,
      Cell: renderActions,
    },
  ];

  const columnsSm: Column<IData>[] = [
    {
      Header: "Projects",
      accessor: "prompt",
      Cell: renderSM,
    },
  ];

  useEffect(() => {
    if (data) {
      setSearchResult(data as IApplication[]);
    }
  }, [data]);

  useEffect(() => {
    if (searchString && data) {
      const res = data.filter(item => {
        return (
          item.name.toLowerCase().includes(searchString.toLowerCase()) ||
          item.id.toLowerCase().includes(searchString.toLowerCase())
        );
      });
      setSearchResult(res as IApplication[]);
    } else {
      setSearchResult(data as IApplication[]);
    }
  }, [searchString, data]);

  const handleClose = () => {
    setShowDeleteAppModal(false);
    setShowEditAppModal(false);
    setCurrentApp(null);
  };

  const { displayError } = useCheckAccess(
    noSubscriptionError,
    deleteMutation.isError,
  );

  const deleteApp = () => {
    deleteMutation.mutate({
      orgId: currentOrganization ? currentOrganization.id : "",
      projId: currentApp ? currentApp.id : "",
    });
  };

  useEffect(() => {
    if (deleteMutation.isSuccess) {
      handleClose();
    }
  }, [deleteMutation.isSuccess]);

  const handleRowClick = useCallback(
    (row: IData) => {
      navigate(`/projects/${row.id}`);
    },
    [navigate],
  );

  return (
    <>
      {showDeleteAppModal && (
        <DeleteApplicationModal
          show={true}
          app={currentApp}
          closeModal={handleClose}
          deleteApplication={deleteApp}
          isProcessing={deleteMutation.isLoading}
          errorMessage={displayError}
          disabledSubmit={deleteMutation.isLoading || !!displayError}
        />
      )}

      {showEditAppModal && (
        <EditApplicationModal
          show={true}
          app={currentApp}
          closeModal={handleClose}
        />
      )}

      <div className="custom-header-wrapper align-items-start">
        <TableSearch
          label={
            <>
              <strong>Search</strong> through all projects
            </>
          }
          placeholder="Type to filter..."
        />
      </div>

      <PaginatedTable
        columns={isLg ? columnsSm : columns}
        data={searchResult || []}
        isLoading={isLoading}
        onRowClick={handleRowClick}
      />
    </>
  );
};

export { ApplicationTable };
